import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Battery,
  Bluetooth,
  ShieldCheck,
  Zap,
  Plug,
  Gauge,
  Sparkles,
  ChevronRight,
  Cpu,
  Mail,
  Phone,
  Activity,
  Wrench,
  CircuitBoard,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Countdown from "@/components/landing/Countdown";
import Testimonials from "@/components/landing/Testimonials";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Inline 4-pointed star — Celestia signature glint
const StarGlint = ({ className = "w-4 h-4" }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    className={className}
    aria-hidden="true"
  >
    <path
      d="M12 1.5 L13.6 10.4 L22.5 12 L13.6 13.6 L12 22.5 L10.4 13.6 L1.5 12 L10.4 10.4 Z"
      fill="currentColor"
    />
  </svg>
);

const Section = ({ id, children, className = "" }) => (
  <section
    id={id}
    className={`relative px-5 sm:px-8 lg:px-16 py-20 sm:py-28 max-w-7xl mx-auto ${className}`}
  >
    {children}
  </section>
);

const Overline = ({ children, testid }) => (
  <div
    className="flex items-center gap-3 mb-5"
    data-testid={testid}
  >
    <span className="text-[#B026FF] glint"><StarGlint className="w-3 h-3" /></span>
    <span className="uppercase tracking-[0.28em] text-[11px] text-[#B026FF] font-semibold">
      {children}
    </span>
  </div>
);

const H2 = ({ children, testid }) => (
  <h2
    className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white max-w-3xl"
    data-testid={testid}
  >
    {children}
  </h2>
);

// ---------- HERO ----------
const Hero = ({ premiereDate, leadsCount }) => {
  const scrollToForm = () => {
    document.getElementById("signup")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 stars-overlay twinkle pointer-events-none" />
      <div className="grain" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-16 pt-28 sm:pt-32 lg:pt-40 pb-16 sm:pb-24">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <Overline testid="hero-overline">Polska Inżynieria · Edycja Limitowana 2026</Overline>

            <h1
              className="text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter leading-[1.02] text-white"
              data-testid="hero-headline"
            >
              <span className="text-white">Celestia Battery —</span>
              <br />
              <span className="text-white">Premium </span>
              <span className="text-[#B026FF] glint">Akumulatory 40Ah</span>
              <br />
              <span className="text-white">do Hulajnóg </span>
              <span className="relative inline-block">
                <span className="text-white">Ausom</span>
                <StarGlint className="absolute -top-2 -right-6 w-5 h-5 text-white glint float-slow" />
              </span>
            </h1>

            <p
              className="mt-7 text-base sm:text-lg text-[#A69CBB] max-w-2xl leading-relaxed"
              data-testid="hero-subheadline"
            >
              Nawet <span className="text-white font-semibold">120 km zasięgu</span> i pełna moc do
              ostatniego kilometra. Wyeliminuj spadek napięcia (voltage sag),
              monitoruj stan ogniw w aplikacji i zyskaj święty spokój dzięki
              polskiej inżynierii.
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <button
                onClick={scrollToForm}
                data-testid="hero-cta-primary"
                className="btn-neon pulse-glow inline-flex items-center justify-center gap-2 px-7 py-4 rounded-md text-[15px] font-semibold tracking-wide"
              >
                <Sparkles className="w-4 h-4" />
                Zarezerwuj akumulator i odbierz 500 zł rabatu
              </button>
              <a
                href="#products"
                data-testid="hero-cta-secondary"
                className="btn-ghost-neon inline-flex items-center justify-center gap-2 px-7 py-4 rounded-md text-[15px] font-medium tracking-wide"
              >
                Zobacz modele <ChevronRight className="w-4 h-4" />
              </a>
            </div>

            {leadsCount > 0 && (
              <div
                className="mt-6 inline-flex items-center gap-2 text-xs text-[#A69CBB]"
                data-testid="hero-social-proof"
              >
                <div className="flex -space-x-2">
                  <span className="w-5 h-5 rounded-full bg-[#B026FF]/80 border border-[#0B0410]" />
                  <span className="w-5 h-5 rounded-full bg-[#D400FF]/80 border border-[#0B0410]" />
                  <span className="w-5 h-5 rounded-full bg-[#7C3AED]/80 border border-[#0B0410]" />
                </div>
                Już <span className="text-white font-bold">{leadsCount}</span> osób
                zarezerwowało rabat 500 zł
              </div>
            )}

            <Countdown targetISO={premiereDate} />

            {/* mini metrics */}
            <div className="mt-12 grid grid-cols-3 gap-4 max-w-xl">
              {[
                { v: "120", l: "km zasięgu" },
                { v: "40Ah", l: "pojemność" },
                { v: "50A", l: "prąd ciągły" },
              ].map((m, i) => (
                <div
                  key={i}
                  className="neon-edge px-4 py-4 bg-[#100913]/60 backdrop-blur"
                  data-testid={`hero-metric-${i}`}
                >
                  <div className="text-3xl font-black text-white tracking-tight">
                    {m.v}
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[#756B8A] mt-1">
                    {m.l}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* right visual */}
          <div className="lg:col-span-5 relative">
            <div className="industrial-card armor-corners relative aspect-[4/5] overflow-hidden rounded-md">
              <span className="corner tl" />
              <span className="corner tr" />
              <span className="corner bl" />
              <span className="corner br" />
              <div className="shine-line" />
              <img
                src="https://images.pexels.com/photos/35673093/pexels-photo-35673093.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=700"
                alt="Akumulator Celestia premium"
                className="absolute inset-0 w-full h-full object-cover opacity-90"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0410] via-[#0B0410]/30 to-transparent" />
              <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-[#0B0410]/70 backdrop-blur border border-[#B026FF]/40">
                <span className="w-1.5 h-1.5 rounded-full bg-[#B026FF] pulse-glow" />
                <span className="text-[10px] uppercase tracking-[0.25em] text-white">
                  LG Cells · Smart BMS
                </span>
              </div>
              <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-[#A69CBB]">Model</div>
                  <div className="text-xl font-bold text-white tracking-tight">Celestia 40Ah</div>
                </div>
                <StarGlint className="w-6 h-6 text-white glint" />
              </div>
            </div>
            {/* glow halo */}
            <div className="absolute -inset-10 -z-10 bg-[radial-gradient(closest-side,rgba(176,38,255,0.35),transparent_70%)] blur-2xl" />
          </div>
        </div>
      </div>
      <div className="section-divider mx-5 sm:mx-8 lg:mx-16" />
    </section>
  );
};

// ---------- VALUE PROP ----------
const VALUE_PROPS = [
  {
    icon: Gauge,
    title: "Zasięg do 120 km",
    body: "Ponad 2x większy dystans niż na fabrycznej baterii. Zapomnij o codziennym ładowaniu.",
  },
  {
    icon: Bluetooth,
    title: "Smart BMS & Aplikacja",
    body: "Inteligentny system zarządzania baterią z Bluetooth. Śledź temperaturę, prąd rozładowania i precyzyjny procent baterii na ekranie telefonu.",
  },
  {
    icon: Battery,
    title: "Ogniwa Premium LG (Polska Produkcja)",
    body: "Brak taniej chińszczyzny. Konstrukcja w bezpiecznej architekturze plastra miodu. Aktywny balanser 1A dbający o żywotność cel przez lata.",
  },
  {
    icon: Plug,
    title: "Instalacja Plug & Play",
    body: "Idealne dopasowanie do fabrycznych wtyczek i ramy Ausom. Złącze anti-spark eliminuje iskrzenie. Modyfikacja w 100% odwracalna.",
  },
];

const ValueProposition = () => (
  <Section id="features">
    <Overline testid="value-overline">Dlaczego Celestia</Overline>
    <H2 testid="value-h2">
      Każdy szczegół zaprojektowany pod
      <span className="text-[#B026FF]"> ekstremalne </span>warunki.
    </H2>
    <p className="mt-5 text-[#A69CBB] max-w-2xl">
      Cztery filary, które oddzielają nasze pakiety od taniej chińskiej masówki.
    </p>

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
      {VALUE_PROPS.map((v, i) => {
        const Icon = v.icon;
        return (
          <div
            key={i}
            className="industrial-card armor-corners relative p-7 rounded-md min-h-[260px]"
            data-testid={`value-card-${i}`}
          >
            <span className="corner tl" />
            <span className="corner tr" />
            <span className="corner bl" />
            <span className="corner br" />
            <div className="shine-line" />

            <div className="w-12 h-12 rounded-sm border border-[#B026FF]/40 bg-[#B026FF]/8 flex items-center justify-center mb-6">
              <Icon className="w-6 h-6 text-[#B026FF]" />
            </div>
            <h3 className="text-lg font-bold text-white tracking-tight">{v.title}</h3>
            <p className="text-sm text-[#A69CBB] mt-3 leading-relaxed">{v.body}</p>
            <div className="absolute bottom-3 right-3 text-[10px] uppercase tracking-[0.25em] text-[#756B8A]">
              0{i + 1}
            </div>
          </div>
        );
      })}
    </div>
  </Section>
);

// ---------- PRODUCTS ----------
const PRODUCTS = [
  {
    id: "celestia-pro-40",
    name: "Celestia Pro 40Ah",
    sub: "Dedykowana do: Ausom Gallop / DT2 Pro",
    specs: [
      "Ogniwa LG (Polska Produkcja)",
      "Smart BMS z Bluetooth + aplikacja",
      "Prąd ciągły 50A · szczyt 120A",
      "Złącze anti-spark · Plug & Play",
    ],
    regular: 4099,
    earlyBird: 3599,
    badge: "Bestseller Pre-order",
    image:
      "https://images.pexels.com/photos/35673093/pexels-photo-35673093.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900",
  },
  {
    id: "celestia-urban-40",
    name: "Celestia Urban 40Ah",
    sub: "Dedykowana do: Ausom Leopard / Gosoul 2 / K20",
    specs: [
      "Ogniwa LG (Polska Produkcja)",
      "Smart BMS z Bluetooth + aplikacja",
      "Idealne dopasowanie do podestu miejskiego",
      "Aktywny balanser 1A · architektura plastra miodu",
    ],
    regular: 4999,
    earlyBird: 4499,
    badge: "Miejski Flagman",
    image:
      "https://images.pexels.com/photos/13870996/pexels-photo-13870996.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900",
  },
];

const ProductCard = ({ p, onReserve }) => (
  <div
    className="industrial-card armor-corners relative rounded-md overflow-hidden flex flex-col"
    data-testid={`product-card-${p.id}`}
  >
    <span className="corner tl" />
    <span className="corner tr" />
    <span className="corner bl" />
    <span className="corner br" />
    <div className="shine-line" />

    <div className="relative aspect-[5/3] overflow-hidden">
      <img
        src={p.image}
        alt={p.name}
        className="w-full h-full object-cover gallery-img"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0410] via-[#0B0410]/40 to-transparent" />
      <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-[#0B0410]/80 backdrop-blur border border-[#B026FF]/45">
        <StarGlint className="w-3 h-3 text-[#B026FF]" />
        <span className="text-[10px] uppercase tracking-[0.25em] text-white">
          {p.badge}
        </span>
      </div>
    </div>

    <div className="p-7 flex-1 flex flex-col">
      <h3 className="text-2xl font-black tracking-tight text-white">{p.name}</h3>
      <p className="text-sm text-[#A69CBB] mt-1">{p.sub}</p>

      <ul className="mt-6 space-y-3">
        {p.specs.map((s, i) => (
          <li key={i} className="flex items-start gap-3 text-sm text-[#C9C1DA]">
            <CircuitBoard className="w-4 h-4 text-[#B026FF] mt-0.5 shrink-0" />
            <span>{s}</span>
          </li>
        ))}
      </ul>

      <div className="mt-7 pt-6 border-t border-white/5 flex items-end justify-between">
        <div>
          <div className="text-xs text-[#756B8A] line-through">
            Cena regularna: {p.regular.toLocaleString("pl-PL")} zł
          </div>
          <div className="text-3xl font-black text-white mt-1">
            {p.earlyBird.toLocaleString("pl-PL")} zł
            <span className="text-xs uppercase tracking-[0.2em] text-[#B026FF] ml-2">
              Early Bird
            </span>
          </div>
        </div>
      </div>

      <button
        onClick={() => onReserve(p.name)}
        data-testid={`product-cta-${p.id}`}
        className="btn-neon mt-7 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-md text-sm font-semibold tracking-wide"
      >
        <Sparkles className="w-4 h-4" />
        Zarezerwuj miejsce na liście Early Bird
      </button>
    </div>
  </div>
);

const Products = ({ onReserve }) => (
  <Section id="products">
    <Overline testid="products-overline">Modele Pre-order</Overline>
    <H2 testid="products-h2">
      Wybierz model dedykowany do
      <span className="text-[#B026FF]"> Twojego Ausoma</span>.
    </H2>
    <p className="mt-5 text-[#A69CBB] max-w-2xl">
      Limitowana pierwsza partia. Cena Early Bird obowiązuje wyłącznie dla osób z listy
      priorytetowej.
    </p>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-7 mt-12">
      {PRODUCTS.map((p) => (
        <ProductCard key={p.id} p={p} onReserve={onReserve} />
      ))}
    </div>

    <div
      className="mt-10 text-center text-sm text-[#A69CBB]"
      data-testid="products-custom-note"
    >
      Nie widzisz swojego modelu?{" "}
      <a
        href="#signup"
        className="text-[#B026FF] underline decoration-dotted underline-offset-4 hover:text-white transition"
      >
        Zjedź na dół i zapytaj o customowy projekt →
      </a>
    </div>
  </Section>
);

// ---------- PRODUCTION ----------
const PRODUCTION_IMAGES = [
  {
    src: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=900&q=80",
    caption:
      "Precyzyjne, grube taśmy czystej miedzi zapewniające zero strat energii i niską temperaturę pracy.",
    label: "Zgrzewanie",
  },
  {
    src: "https://images.unsplash.com/photo-1562408590-e32931084e23?auto=format&fit=crop&w=900&q=80",
    caption:
      "Podgląd parametrów na żywo. Pełna kontrola nad temperaturą i napięciem każdej sekcji.",
    label: "Smart BMS",
  },
  {
    src: "https://images.unsplash.com/photo-1717386255773-1e3037c81788?auto=format&fit=crop&w=900&q=80",
    caption:
      "Ekstremalne testy drogowe. Sprawdziliśmy nasze pakiety w najtrudniejszych warunkach terenowych.",
    label: "Testy 125 km",
  },
];

const ProductionGallery = () => (
  <Section id="production">
    <Overline testid="production-overline">Transparentna Produkcja</Overline>
    <H2 testid="production-h2">
      Inżynieria bez <span className="text-[#B026FF]">kompromisów</span>.
    </H2>
    <p className="mt-5 text-[#A69CBB] max-w-2xl">
      Zobacz, jak powstają najbezpieczniejsze akumulatory na polskim rynku.
      Projektujemy, zgrzewamy i testujemy w naszym warsztacie.
    </p>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-12">
      {PRODUCTION_IMAGES.map((img, i) => (
        <div
          key={i}
          className="industrial-card armor-corners relative rounded-md overflow-hidden group"
          data-testid={`production-card-${i}`}
        >
          <span className="corner tl" />
          <span className="corner tr" />
          <span className="corner bl" />
          <span className="corner br" />

          <div className="relative aspect-[4/5] overflow-hidden">
            <img
              src={img.src}
              alt={img.label}
              className="w-full h-full object-cover gallery-img"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0410] via-[#0B0410]/30 to-transparent" />
            <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-[#0B0410]/80 backdrop-blur border border-[#B026FF]/45">
              <span className="w-1.5 h-1.5 rounded-full bg-[#B026FF]" />
              <span className="text-[10px] uppercase tracking-[0.25em] text-white">
                {img.label}
              </span>
            </div>
          </div>
          <div className="p-6">
            <p className="text-sm text-[#A69CBB] leading-relaxed">{img.caption}</p>
          </div>
        </div>
      ))}
    </div>
  </Section>
);

// ---------- FAQ ----------
const FAQ_ITEMS = [
  {
    q: "Naczytałem się o płonących hulajnogach. Czy Wasza bateria jest bezpieczna?",
    a: "Bezpieczeństwo to nasz absolutny priorytet, na którym nie oszczędzamy. Nasze pakiety budujemy w technologii „plastra miodu” – każde ogniwo premium LG jest fizycznie odizolowane od sąsiedniego, co eliminuje ryzyko zwarcia kaskadowego. Nad wszystkim czuwa zaawansowany Smart BMS z Bluetooth, który w ułamku sekundy odetnie zasilanie, jeśli wykryje najmniejsze odstępstwo od normy, temperaturę powyżej limitu lub przeciążenie.",
  },
  {
    q: "Czy montaż baterii Celestia oznacza utratę fabrycznej gwarancji na hulajnogę Ausom?",
    a: "Nasze akumulatory są zaprojektowane w standardzie Plug & Play. Oznacza to, że pasują idealnie w fabryczne miejsca i posiadają oryginalne złącza Ausom (w tym złącze anti-spark ochroniejące elektronikę). Nie tniesz żadnych kabli, nie lutujesz. Montaż jest w 100% odwracalny – w razie potrzeby możesz w 15 minut wrócić do fabrycznej baterii, bez śladu jakiejkolwiek modyfikacji.",
  },
  {
    q: "Po co mi aplikacja mobilna i Bluetooth w baterii?",
    a: "Dla pełnej transparentności. Fabryczne wskaźniki w hulajnogach pokazują napięcie w sposób orientacyjny – często hulajnoga wyłącza się, mimo że pokazywała „dwie kreski”. Nasza aplikacja na smartfona pozwala Ci monitorować realny stan zdrowia baterii (SOH), temperaturę pakietu, zużycie prądu w czasie rzeczywistym oraz precyzyjny procent naładowania. Ty rządzisz swoją energią.",
  },
];

const FAQ = () => (
  <Section id="faq">
    <Overline testid="faq-overline">Częste obawy klientów</Overline>
    <H2 testid="faq-h2">
      Często zadawane <span className="text-[#B026FF]">pytania</span>.
    </H2>

    <div className="mt-12 max-w-3xl">
      <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
        {FAQ_ITEMS.map((f, i) => (
          <AccordionItem
            key={i}
            value={`faq-${i}`}
            className="industrial-card rounded-md px-6 border data-[state=open]:neon-edge-active"
            data-testid={`faq-item-${i}`}
          >
            <AccordionTrigger className="text-left text-white hover:no-underline py-5 [&[data-state=open]>svg]:text-[#B026FF]">
              <span className="flex items-start gap-3">
                <StarGlint className="w-3 h-3 text-[#B026FF] mt-2 shrink-0" />
                <span className="text-base sm:text-lg font-semibold">{f.q}</span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="text-[#C9C1DA] text-sm leading-relaxed pb-6 pl-6">
              {f.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </Section>
);

// ---------- SIGN UP FORM ----------
const SCOOTER_MODELS = [
  "Ausom Gallop",
  "Ausom DT2 Pro",
  "Ausom Leopard",
  "Ausom Gosoul 2",
  "Ausom K20",
  "Inny model / Chcę baterię customową",
];

const Signup = ({ presetNote, clearPresetNote }) => {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [scooterModel, setScooterModel] = useState("");
  const [notes, setNotes] = useState(presetNote || "");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // sync preset when changed
  useEffect(() => {
    if (presetNote) setNotes(presetNote);
  }, [presetNote]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !scooterModel) {
      toast.error("Uzupełnij wymagane pola: email i model hulajnogi.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        email,
        phone: phone || null,
        scooter_model: scooterModel,
        notes: notes || null,
      });
      setSuccess(true);
      toast.success("Jesteś na liście Early Bird. Rabat 500 zł zaklepany.");
      setEmail("");
      setPhone("");
      setScooterModel("");
      setNotes("");
      if (clearPresetNote) clearPresetNote();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d.msg).join(" · ")
          : "Coś poszło nie tak. Spróbuj ponownie.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section id="signup">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-5">
          <Overline testid="signup-overline">Lista Priorytetowa</Overline>
          <H2 testid="signup-h2">
            Dołącz do listy <span className="text-[#B026FF]">Early Bird</span>.
          </H2>
          <p className="mt-5 text-[#A69CBB] leading-relaxed">
            Zapis jest całkowicie <span className="text-white font-semibold">DARMOWY</span> i{" "}
            <span className="text-white font-semibold">NIEZOBOWIĄZUJĄCY</span>. Gwarantuje Ci
            otrzymanie <span className="text-[#B026FF] font-semibold">500 zł zniżki</span> na
            pierwszą, limitowaną partię akumulatorów z certyfikatem oraz pierwszeństwo
            dostawy przed rozpoczęciem sezonu.
          </p>

          <ul className="mt-8 space-y-4">
            {[
              { icon: ShieldCheck, t: "Zerowe ryzyko — bez zaliczki" },
              { icon: Zap, t: "Pierwszeństwo dostawy w pierwszej partii" },
              { icon: Activity, t: "Powiadomienie SMS na 48h przed premierą" },
            ].map((b, i) => {
              const Icon = b.icon;
              return (
                <li key={i} className="flex items-center gap-3 text-sm text-[#C9C1DA]">
                  <Icon className="w-5 h-5 text-[#B026FF]" />
                  {b.t}
                </li>
              );
            })}
          </ul>
        </div>

        <div className="lg:col-span-7 w-full">
          <form
            onSubmit={submit}
            data-testid="signup-form"
            className="industrial-card armor-corners relative rounded-md p-6 sm:p-9 space-y-5"
          >
            <span className="corner tl" />
            <span className="corner tr" />
            <span className="corner bl" />
            <span className="corner br" />

            {success && (
              <div
                className="mb-2 p-4 rounded-md border border-[#B026FF]/45 bg-[#B026FF]/10 text-sm text-white"
                data-testid="signup-success"
              >
                <div className="font-semibold flex items-center gap-2">
                  <StarGlint className="w-3 h-3" /> Rezerwacja zaklepana!
                </div>
                <div className="text-[#C9C1DA] mt-1">
                  Otrzymasz powiadomienie z indywidualnym kodem rabatowym 500 zł w dniu premiery.
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] text-[#A69CBB]">
                Adres E-mail *
              </Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#756B8A]" />
                <Input
                  id="email"
                  data-testid="signup-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="twoj@email.pl"
                  className="bg-[#100913]/60 border-white/8 text-white pl-10 h-12 focus-visible:ring-[#B026FF] focus-visible:border-[#B026FF]/60"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="phone" className="text-xs uppercase tracking-[0.2em] text-[#A69CBB]">
                Numer telefonu (opcjonalnie)
              </Label>
              <div className="relative mt-2">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#756B8A]" />
                <Input
                  id="phone"
                  data-testid="signup-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+48 600 000 000"
                  className="bg-[#100913]/60 border-white/8 text-white pl-10 h-12 focus-visible:ring-[#B026FF] focus-visible:border-[#B026FF]/60"
                />
              </div>
              <p className="text-[11px] text-[#756B8A] mt-1.5">
                Wykorzystamy tylko do powiadomienia SMS przed premierą.
              </p>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#A69CBB]">
                Posiadany model hulajnogi Ausom *
              </Label>
              <Select value={scooterModel} onValueChange={setScooterModel}>
                <SelectTrigger
                  data-testid="signup-model"
                  className="mt-2 bg-[#100913]/60 border-white/8 text-white h-12 focus:ring-[#B026FF] focus:border-[#B026FF]/60"
                >
                  <SelectValue placeholder="Wybierz swój model…" />
                </SelectTrigger>
                <SelectContent className="bg-[#18111E] border-[#B026FF]/30 text-white">
                  {SCOOTER_MODELS.map((m) => (
                    <SelectItem
                      key={m}
                      value={m}
                      className="focus:bg-[#B026FF]/15 focus:text-white"
                      data-testid={`signup-model-option-${m.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                    >
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="notes" className="text-xs uppercase tracking-[0.2em] text-[#A69CBB]">
                Uwagi / pytania do inżynierów
              </Label>
              <Textarea
                id="notes"
                data-testid="signup-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Np. interesuje mnie wersja customowa, mam pytanie o BMS… (Serwisy: dopisz [SERWIS] dla cennika hurtowego)"
                rows={4}
                className="mt-2 bg-[#100913]/60 border-white/8 text-white focus-visible:ring-[#B026FF] focus-visible:border-[#B026FF]/60"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="signup-submit"
              className="btn-neon pulse-glow w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-md text-sm font-bold tracking-wide uppercase disabled:opacity-60"
            >
              <Sparkles className="w-4 h-4" />
              {loading ? "Zapisuję…" : "Zaklepuję Rabat 500 zł i Miejsce Na Liście"}
            </button>

            <p className="text-[11px] text-[#756B8A] text-center leading-relaxed">
              Zapisując się akceptujesz naszą Politykę Prywatności. Nie spamujemy.
              W każdej chwili możesz się wypisać.
            </p>
          </form>
        </div>
      </div>
    </Section>
  );
};

// ---------- NAV ----------
const Nav = () => (
  <nav className="fixed top-0 inset-x-0 z-40 backdrop-blur-xl bg-[#0B0410]/70 border-b border-white/5">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-16 h-16 flex items-center justify-between">
      <a href="#" className="flex items-center gap-2.5" data-testid="nav-logo">
        <StarGlint className="w-4 h-4 text-white glint" />
        <span className="text-white font-black tracking-tight text-base">
          CELESTIA<span className="text-[#B026FF]">.</span>
        </span>
      </a>
      <div className="hidden md:flex items-center gap-8 text-sm text-[#A69CBB]">
        <a href="#features" className="hover:text-white transition" data-testid="nav-features">Funkcje</a>
        <a href="#products" className="hover:text-white transition" data-testid="nav-products">Modele</a>
        <a href="#production" className="hover:text-white transition" data-testid="nav-production">Produkcja</a>
        <a href="#faq" className="hover:text-white transition" data-testid="nav-faq">FAQ</a>
      </div>
      <a
        href="#signup"
        data-testid="nav-cta"
        className="btn-ghost-neon px-4 py-2 rounded-md text-xs font-semibold uppercase tracking-wider"
      >
        Early Bird
      </a>
    </div>
  </nav>
);

// ---------- FOOTER ----------
const Footer = () => (
  <footer className="relative mt-10 border-t border-white/5 bg-[#080309]" data-testid="footer">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-16 py-14">
      <div className="grid md:grid-cols-3 gap-10">
        <div>
          <div className="flex items-center gap-2.5">
            <StarGlint className="w-4 h-4 text-white glint" />
            <span className="text-white font-black tracking-tight text-base">
              CELESTIA<span className="text-[#B026FF]">.</span>
            </span>
          </div>
          <p className="text-sm text-[#A69CBB] mt-4 max-w-xs leading-relaxed">
            Premium akumulatory 40Ah do hulajnóg Ausom. Projektowane i zgrzewane w Polsce.
          </p>
        </div>

        <div
          className="industrial-card rounded-md p-6 md:col-span-2"
          data-testid="b2b-section"
        >
          <div className="flex items-center gap-2 mb-2">
            <Wrench className="w-4 h-4 text-[#B026FF]" />
            <span className="text-xs uppercase tracking-[0.25em] text-[#B026FF] font-semibold">
              Partnerstwo B2B
            </span>
          </div>
          <p className="text-sm text-[#C9C1DA] leading-relaxed">
            Prowadzisz serwis hulajnóg elektrycznych? Szukamy partnerów B2B na terenie
            całej Polski. Napisz do nas przez formularz, dopisując w uwagach{" "}
            <span className="text-white font-semibold">[SERWIS]</span>, aby otrzymać
            cennik hurtowy.
          </p>
        </div>
      </div>

      <div className="mt-10 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-[#756B8A]">
        <div>© {new Date().getFullYear()} Celestia Battery. Wszystkie prawa zastrzeżone.</div>
        <div className="flex gap-6">
          <a href="#" className="hover:text-white transition" data-testid="footer-privacy">
            Polityka Prywatności
          </a>
          <a
            href="mailto:kontakt@celestiabattery.pl"
            className="hover:text-white transition"
            data-testid="footer-contact"
          >
            kontakt@celestiabattery.pl
          </a>
        </div>
      </div>
    </div>
  </footer>
);

// ---------- MAIN LANDING ----------
export default function Landing() {
  const [presetNote, setPresetNote] = useState("");
  const [config, setConfig] = useState({ premiere_date: null, leads_count: 0 });

  useEffect(() => {
    axios
      .get(`${API}/config`)
      .then((r) => setConfig(r.data || {}))
      .catch(() => {});
  }, []);

  const handleReserve = (productName) => {
    setPresetNote(`Interesuje mnie model: ${productName}`);
    setTimeout(() => {
      document.getElementById("signup")?.scrollIntoView({ behavior: "smooth" });
      document.getElementById("signup-email")?.focus();
    }, 50);
  };

  return (
    <div className="relative min-h-screen space-bg" data-testid="landing-root">
      {/* Decorative aurora */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[1100px] h-[700px] bg-[radial-gradient(closest-side,rgba(176,38,255,0.25),transparent_70%)] blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[700px] h-[500px] bg-[radial-gradient(closest-side,rgba(217,70,239,0.18),transparent_70%)] blur-3xl" />
      </div>

      <Nav />
      <Hero
        premiereDate={config.premiere_date}
        leadsCount={config.leads_count || 0}
      />
      <ValueProposition />
      <Products onReserve={handleReserve} />
      <ProductionGallery />
      <Testimonials />
      <FAQ />
      <Signup presetNote={presetNote} clearPresetNote={() => setPresetNote("")} />
      <Footer />
    </div>
  );
}
